import Editor, { type Monaco, type OnMount } from "@monaco-editor/react";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { TestVariable } from "../types";

/**
 * Deduplicate variables by key, keeping the first occurrence (highest precedence).
 * Variables should be passed in precedence order: global > app_config > check-specific
 */
const deduplicateVariables = (variables: TestVariable[]): TestVariable[] => {
  const seen = new Set<string>();
  return variables.filter((v) => {
    if (seen.has(v.key)) return false;
    seen.add(v.key);
    return true;
  });
};

// Monaco editor types
type IStandaloneCodeEditor = Parameters<OnMount>[0];

// Global registry for variable providers
// This prevents duplicate registrations when multiple editors mount
const registeredLanguages = new Set<string>();
const globalVariablesMap = new Map<string, TestVariable[]>();

// Helper to get deduplicated variables from all editors
const getGlobalDeduplicatedVariables = (): TestVariable[] => {
  const allVariables = Array.from(globalVariablesMap.values()).flat();
  return deduplicateVariables(allVariables);
};

const registerGlobalProviders = (
  monaco: Monaco,
  language: string,
  getDisplayValue: (variable: TestVariable) => string,
) => {
  if (registeredLanguages.has(language)) return;
  registeredLanguages.add(language);

  // Register completion provider
  monaco.languages.registerCompletionItemProvider(language, {
    triggerCharacters: ["{"],
    provideCompletionItems: (model, position) => {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const lastDoubleBrace = textUntilPosition.lastIndexOf("{{");
      if (lastDoubleBrace === -1) {
        return { suggestions: [] };
      }

      const textAfterBrace = textUntilPosition.slice(lastDoubleBrace + 2);
      if (textAfterBrace.includes("}}")) {
        return { suggestions: [] };
      }

      const searchText = textAfterBrace.toLowerCase();
      const deduped = getGlobalDeduplicatedVariables();

      const suggestions = deduped
        .filter((v) => searchText === "" || v.key.toLowerCase().includes(searchText))
        .map((variable) => ({
          label: variable.key,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: `${variable.key}}}`,
          detail: `(${variable.context})`,
          documentation: {
            value: `**Value:** ${getDisplayValue(variable)}`,
          },
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column - searchText.length,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          },
          sortText: variable.key,
        }));

      return { suggestions };
    },
  });

  // Register hover provider
  monaco.languages.registerHoverProvider(language, {
    provideHover: (model, position) => {
      const word = model.getWordAtPosition(position);
      if (!word) return null;

      // Get the full line and check if we're inside {{...}}
      const lineContent = model.getLineContent(position.lineNumber);
      const beforeWord = lineContent.substring(0, word.startColumn - 1);
      const afterWord = lineContent.substring(word.endColumn - 1);

      // Check if this word is inside {{ }}
      const openBraceIndex = beforeWord.lastIndexOf("{{");
      const closeBraceIndex = afterWord.indexOf("}}");

      if (openBraceIndex === -1 || closeBraceIndex === -1) return null;

      // Check there's no }} between {{ and the word
      const textBetween = beforeWord.substring(openBraceIndex + 2);
      if (textBetween.includes("}}")) return null;

      // Look up variable from global deduplicated map
      const deduped = getGlobalDeduplicatedVariables();
      const variableName = word.word;
      const variable = deduped.find((v) => v.key === variableName);

      if (!variable) return null;

      return {
        range: new monaco.Range(
          position.lineNumber,
          word.startColumn,
          position.lineNumber,
          word.endColumn,
        ),
        contents: [
          {
            value: `**${variable.key}** _(${variable.context})_`,
          },
          {
            value: `Value: \`${getDisplayValue(variable)}\``,
          },
        ],
      };
    },
  });
};

interface VariableMonacoEditorProps {
  value: string;
  onChange: (value: string) => void;
  variables: TestVariable[];
  language?: string;
  height?: string | number;
  label?: string;
  placeholder?: string;
  helperText?: string;
  readOnly?: boolean;
}

// Regex to find {{variable}} patterns (hoisted to module scope per React best practices)
const VARIABLE_PATTERN = /\{\{([^}]+)\}\}/g;

// Counter for generating unique editor IDs
let editorIdCounter = 0;

export const VariableMonacoEditor: React.FC<VariableMonacoEditorProps> = ({
  value,
  onChange,
  variables,
  language = "json",
  height = 150,
  label,
  helperText,
  readOnly = false,
}) => {
  const editorRef = useRef<IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const disposablesRef = useRef<{ dispose: () => void }[]>([]);

  // Generate a unique ID for this editor instance
  const editorIdRef = useRef<string>(`editor-${editorIdCounter++}`);

  // Deduplicate variables by key, keeping highest precedence (first occurrence)
  const deduplicatedVariables = useMemo(() => deduplicateVariables(variables), [variables]);

  // Update the global variables map when this editor's variables change
  useEffect(() => {
    const editorId = editorIdRef.current;
    globalVariablesMap.set(editorId, deduplicatedVariables);
    return () => {
      globalVariablesMap.delete(editorId);
    };
  }, [deduplicatedVariables]);

  // Update decorations when value changes
  const updateDecorations = useCallback(() => {
    if (!editorRef.current || !monacoRef.current) return;

    const model = editorRef.current.getModel();
    if (!model) return;

    const decorations: {
      range: InstanceType<typeof monacoRef.current.Range>;
      options: { inlineClassName: string };
    }[] = [];
    const text = model.getValue();

    const regex = new RegExp(VARIABLE_PATTERN);
    for (let match = regex.exec(text); match !== null; match = regex.exec(text)) {
      const startPos = model.getPositionAt(match.index);
      const endPos = model.getPositionAt(match.index + match[0].length);

      decorations.push({
        range: new monacoRef.current.Range(
          startPos.lineNumber,
          startPos.column,
          endPos.lineNumber,
          endPos.column,
        ),
        options: {
          inlineClassName: "variable-highlight",
        },
      });
    }

    decorationsRef.current = editorRef.current.deltaDecorations(
      decorationsRef.current,
      decorations,
    );
  }, []);

  // Update decorations when value changes
  useEffect(() => {
    updateDecorations();
  }, [updateDecorations]);

  // Cleanup disposables on unmount
  useEffect(() => {
    return () => {
      for (const d of disposablesRef.current) {
        d.dispose();
      }
      disposablesRef.current = [];
    };
  }, []);

  const getDisplayValue = useCallback((variable: TestVariable) => {
    if (variable.is_sensitive) {
      return "\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
    }
    return String(variable.value);
  }, []);

  const handleFormat = useCallback(() => {
    if (!editorRef.current) return;

    // Trigger format action
    editorRef.current.getAction("editor.action.formatDocument")?.run();
  }, []);

  const handleEditorMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      // Clean up previous disposables (in case modal is reopened)
      for (const d of disposablesRef.current) {
        d.dispose();
      }
      disposablesRef.current = [];

      // Register global providers (completion + hover, only once per language)
      registerGlobalProviders(monaco, language, getDisplayValue);

      // Add custom CSS for variable highlighting
      const styleId = "variable-monaco-highlight-styles";
      if (!document.getElementById(styleId)) {
        const styleElement = document.createElement("style");
        styleElement.id = styleId;
        styleElement.textContent = `
          .variable-highlight {
            background-color: rgba(103, 58, 183, 0.15);
            border-radius: 3px;
            color: #673ab7;
            font-weight: 500;
          }
        `;
        document.head.appendChild(styleElement);
      }

      // Update decorations after mount
      updateDecorations();

      // Update decorations on content change
      const contentChangeDisposable = editor.onDidChangeModelContent(() => {
        updateDecorations();
      });

      // Store disposables for cleanup (providers are global, not per-editor)
      disposablesRef.current = [contentChangeDisposable];
    },
    [language, getDisplayValue, updateDecorations],
  );

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {!readOnly && (
            <Tooltip title="Format JSON">
              <IconButton size="small" onClick={handleFormat} sx={{ p: 0.25 }}>
                <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          "&:hover": {
            borderColor: "text.primary",
          },
          "&:focus-within": {
            borderColor: "primary.main",
            borderWidth: 2,
          },
        }}
      >
        <Editor
          height={height}
          language={language}
          value={value}
          onChange={(newValue) => onChange(newValue || "")}
          onMount={handleEditorMount}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            lineNumbers: "off",
            wordWrap: "on",
            wrappingIndent: "same",
            readOnly,
            folding: false,
            lineDecorationsWidth: 12,
            lineNumbersMinChars: 0,
            glyphMargin: false,
            padding: { top: 12, bottom: 12 },
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
            automaticLayout: true,
            tabSize: 2,
            fixedOverflowWidgets: true,
            formatOnPaste: true,
            formatOnType: false,
            quickSuggestions: {
              other: true,
              comments: false,
              strings: true,
            },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: "on",
            wordBasedSuggestions: "off",
          }}
        />
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default VariableMonacoEditor;
